import { Skeleton, useToast } from '@chakra-ui/react';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Combobox } from '@/components/ui/shared/combobox/Combobox';
import { IOption } from '@/components/ui/shared/DropdownSelect/DropdownSelect';
import { IToastType } from '@/enums/ToastType';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import useSession from '@/hooks/useSession';
import { useStores } from '@/hooks/useStores';
import { useResource } from '@/lib/datx-jsonapi-react';
import { timezonesList } from '@/lib/time-zones';
import { queryThinEvent } from '@/queries/thin-events';

interface IUrlQueryStringParams {
  timeZone?: string;
  day?: string;
  eventId?: string;
}

export const ScheduleTimeZoneFilter: FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { store } = useStores();

  const { thinProfile: profile } = useSession();

  const { timeZone, change, eventId } = useQueryParams<IUrlQueryStringParams>({
    timeZone: StringParam,
    eventId: StringParam,
  });
  const { data: event } = useResource(queryThinEvent(eventId));

  const [itemsList, setItemsList] = useState(() => timezonesList);

  const timeZones = useMemo(() => {
    if (!timeZone) {
      return [];
    }

    const { timeZone: browserTimezone } = Intl.DateTimeFormat().resolvedOptions();

    return timezonesList.reduce(
      (acc, zone) => {
        if (!zone) {
          return [...acc];
        }

        return [...acc, { id: zone, value: zone }];
      },
      [{ id: timeZone || browserTimezone, value: timeZone || browserTimezone }]
    ) as Array<IOption>;
  }, [timeZone]);

  const selectedOption = useMemo(() => timeZones?.find((item) => item.id === timeZone), [timeZone, timeZones]);

  const onChangeItem = async (item: string) => {
    if (!timeZone) {
      return;
    }
    if (!profile) {
      change({ timeZone: item, day: null }, { method: 'replace', shallow: true });
      return;
    }
    try {
      await store.request(`/thin_profiles/${profile?.id}/timezones`, 'POST', {
        data: {
          type: 'timezone',
          attributes: {
            time_zone: item,
            event_id: event?.id,
          },
        },
      });
      change({ timeZone: item, day: null }, { method: 'replace', shallow: true });
    } catch (error) {
      toast({ status: IToastType.ERROR, title: t('eventTimezoneSavingError') });
    }
  };

  const onSearchChange = useCallback((inputValue: string) => {
    const filteredItemsList = timezonesList.filter((item) => item.toLowerCase().includes(inputValue.toLowerCase()));

    setItemsList(filteredItemsList);
  }, []);

  if (!timeZones) {
    return <Skeleton w="200px" h="40px" />;
  }

  return (
    <Combobox
      maxW={['300px', '444px']}
      itemsList={itemsList}
      placeholder={t('schedule.search.dropdown.placeholder')}
      onChangeItem={onChangeItem}
      onSearchChange={onSearchChange}
      selectedOption={selectedOption?.value || timeZones[0]?.value}
    />
  );
};
